import type { SnapPricingBasis } from '@calculations/round/decimal';
import { EditableSnapQuote } from '@snap-types/actions/edit-snap-quote';
import { DocumentWithUser } from '@snap-types/models/document';
import { Decimal } from '@snap-types/types/decimal';
import { priceStrategyUIConfig } from '@ui/config/price';

import { ProductOptionType } from '../../types/state/product-option';
import { QuoteLineType } from '../../types/state/quote-line';
import { QuotePriceType } from '../../types/state/quote-price';
import { emptyLastWin } from './last-win';
import { transformSnapQuotePriceToStoreState } from './transform-quote-price';
import { transformSnapWorkOrderToStoreState } from './transform-work-order';

/**
 * Transforms a quote line without last win or inventory
 */
function transformPartialQuoteLine(
  quoteLine: Omit<
    EditableSnapQuote['latestVersion']['snapQuoteLines'][number],
    | 'lastWin'
    | 'inventory'
    | 'discountPrices'
    | 'custPriceEffects'
    | 'marketPrices'
    | 'partSpecs'
    | 'craftProductOptions'
  >,
  pricingBasis: SnapPricingBasis,
): Omit<
  QuoteLineType,
  | 'lastWin'
  | 'inventory'
  | 'discountPrices'
  | 'custPriceEffects'
  | 'marketPrices'
  | 'partSpecs'
  | 'craftProductOptions'
  | 'estQtyOnHand'
> {
  const quotePriceUuids: string[] = [];
  const quotePrices: Record<string, QuotePriceType> = {};
  const documentIds: string[] = [];
  const documents: Record<string, DocumentWithUser> = {};
  const productOptions: Record<string, ProductOptionType> = {};
  const productOptionsUuids: string[] = [];

  const priceStrategy = quoteLine.priceStrategy;
  const initialInfoTab = priceStrategyUIConfig[priceStrategy].tabs[0];

  let totalLinePrice = new Decimal(0);

  for (const snapQuotePrice of quoteLine.snapQuotePrices) {
    const { uuid, quotePrice, totalPrice } =
      transformSnapQuotePriceToStoreState(snapQuotePrice, pricingBasis);

    quotePriceUuids.push(uuid);
    quotePrices[uuid] = quotePrice;

    totalLinePrice = totalLinePrice.plus(totalPrice);
  }

  for (const document of quoteLine.documents) {
    const { id, fileName, fileType, createdBy, createdAt } = document;
    documentIds.push(id);
    documents[id] = {
      id,
      fileName,
      fileType,
      createdBy: createdBy
        ? {
            displayName: createdBy.displayName,
          }
        : null,
      createdAt,
    };
  }

  for (const productOption of quoteLine.snapProductOptions) {
    const { uuid, code, groupName, optionName, subOptionName } = productOption;
    productOptionsUuids.push(uuid);
    productOptions[uuid] = {
      uuid,
      code: code ?? null,
      groupName,
      optionName,
      subOptionName: subOptionName ?? null,
    };
  }

  return {
    uuid: quoteLine.uuid,
    automatable: quoteLine.automatable,
    priceStrategy: quoteLine.priceStrategy,

    lineNumber: quoteLine.lineNumber,
    customerPartId: quoteLine.customerPartId,
    leadTimeNotes: quoteLine.leadTimeNotes,
    leadTimeSuggestionStatus: quoteLine.leadTimeSuggestionStatus,
    suggestedPricesStatus: quoteLine.suggestedPricesStatus,
    specs: quoteLine.specs,
    description: quoteLine.part?.description,
    partId: quoteLine.part?.partId,
    partCreatedBySnap: quoteLine.part?.createdBySnap ?? false,
    erpDB: quoteLine.part?.erpDB,
    serviceChargeUuid: quoteLine.serviceChargeUuid,

    serviceChargeId: quoteLine.serviceCharge?.id,
    serviceChargeDescription: quoteLine.serviceCharge?.description,

    type: quoteLine.part?.type,
    material: quoteLine.part?.material,
    finishedPartId: quoteLine.finishedPartId,
    configuratorSnapshot: quoteLine.configuratorSnapshot,

    lastWinOrderLineId: quoteLine.lastWinOrderLineId ?? null,

    quotePriceUuids,
    quotePrices,

    documentIds,
    documents,

    status: 'ready',
    isInfoVisible: false,
    infoTabOrderHistoryFilterByCustomer: false,
    infoTabQuoteHistoryFilterByCustomer: false,
    infoTabOrderHistoryExactMatch: false,
    infoTabQuoteHistoryExactMatch: false,
    infoTab: initialInfoTab,

    priceAdjustment: quoteLine.priceAdjustment ?? null,
    priceAdjustmentAmount: quoteLine.priceAdjustmentAmount ?? null,
    priceSegments: quoteLine.priceSegments,

    snapWorkOrder: transformSnapWorkOrderToStoreState(quoteLine.snapWorkOrder),

    productOptionsChangeUnitPrice: quoteLine.productOptionsChangeUnitPrice,
    productOptionsUuids,
    productOptions,

    productComplexityRanking: quoteLine.productComplexityRanking ?? undefined,
    partSite: quoteLine.partSite,

    leadTimeConfig: quoteLine.leadTimeConfig ?? null,
    quantityAwareInputs: quoteLine.quantityAwareInputs ?? null,
  };
}

/**
 * Transforms a partial quote line into store state, adding necessary defaults
 */
export function transformPartialQuoteLineWithDefaults(
  quoteLine: Omit<
    EditableSnapQuote['latestVersion']['snapQuoteLines'][number],
    | 'lastWin'
    | 'inventory'
    | 'discountPrices'
    | 'custPriceEffects'
    | 'marketPrices'
    | 'partSpecs'
    | 'craftProductOptions'
  >,
  pricingBasis: SnapPricingBasis,
): QuoteLineType {
  const transformedQuoteLine = transformPartialQuoteLine(
    quoteLine,
    pricingBasis,
  );

  return {
    ...transformedQuoteLine,
    estQtyOnHand: null,
    lastWin: emptyLastWin(),
    inventory: null,
    discountPrices: [],
    custPriceEffects: [],
    marketPrices: [],
    partSpecs: null,
    craftProductOptions: null,
    productOptionsChangeUnitPrice: false,
  };
}

export function transformSnapQuoteLineToStoreState(
  quoteLine: EditableSnapQuote['latestVersion']['snapQuoteLines'][number],
  pricingBasis: SnapPricingBasis,
): {
  uuid: string;
  quoteLine: QuoteLineType;
} {
  let lastWin = emptyLastWin();

  if (quoteLine.lastWin) {
    lastWin = {
      hasLastWin: true,
      orderId: quoteLine.lastWin.orderId,
      orderDate: quoteLine.lastWin.orderDate,
      wonAt: quoteLine.lastWin.wonAt,
      leadTimeDays: quoteLine.lastWin.leadTime,
      quantity: quoteLine.lastWin.quantity,
      unitPrice: quoteLine.lastWin.unitPrice,
      grossMargin: quoteLine.lastWin.grossMargin,
      contributionMargin: quoteLine.lastWin.contributionMargin,
      unitCost: quoteLine.lastWin.unitCost,
      unitLaborCost: quoteLine.lastWin.unitLaborCost,
      unitMaterialCost: quoteLine.lastWin.unitMaterialCost,
      unitServiceCost: quoteLine.lastWin.unitServiceCost,
      unitVariableCosts: quoteLine.lastWin.unitVariableCosts,
    };
  }

  const estQtyOnHand = quoteLine.inventory?.qtyOnHand?.toNumber();

  const result = {
    uuid: quoteLine.uuid,
    quoteLine: {
      ...transformPartialQuoteLine(quoteLine, pricingBasis),
      estQtyOnHand,
      lastWin,
      inventory: quoteLine.inventory,
      discountPrices: quoteLine.discountPrices,
      custPriceEffects: quoteLine.custPriceEffects,
      marketPrices: quoteLine.marketPrices,
      partSpecs: quoteLine.partSpecs,
      craftProductOptions: quoteLine.craftProductOptions,
    },
  };

  return result;
}
